import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { academyApi } from "@/lib/academy";

export function AcademyCapstone() {
  const [ov, setOv] = useState(null);
  const [texts, setTexts] = useState({});
  const [msgs, setMsgs] = useState({});
  useEffect(() => { academyApi.overview().then(setOv).catch(() => {}); }, []);
  const capstone = ov?.labs?.find((l) => l.id === "LAB-BK");

  async function submit(labId) {
    try {
      await academyApi.submitLab(labId, texts[labId] || "");
      setMsgs({ ...msgs, [labId]: "Submitted for reviewer grading — the Black Belt capstone includes an oral defense." });
    } catch (e) {
      setMsgs({ ...msgs, [labId]: e.response?.data?.detail || "Failed." });
    }
  }

  return (
    <>
      <Seo title="Labs & capstone | Solar Black Belt Academy" description="Eight practical labs and the warehouse capstone with oral defense — the hands-on side of the Solar Black Belt Academy." />
      <section className="page-hero"><div className="wrap"><span className="eyebrow">PRACTICAL LABS</span><h1>Do the work.</h1></div></section>
      <section className="wrap academy-section">
        <p className="lead" style={{ maxWidth: 760 }}>
          Every belt has a practical lab graded by a human reviewer against its rubric. The Black Belt capstone is
          the fictional warehouse case — full assessment, then an oral defense.
        </p>
        {(ov?.labs || []).map((lab) => (
          <div key={lab.id} className="academy-belt-card" style={{ marginTop: 18 }} data-testid={`academy-capstone-${lab.id}`}>
            <span className="academy-belt-chip">{lab.belt} Belt lab</span>
            <h3>{lab.title}</h3>
            <p>{lab.brief}</p>
            <textarea rows={4} maxLength={8000} value={texts[lab.id] || ""} onChange={(e) => setTexts({ ...texts, [lab.id]: e.target.value })} style={{ width: "100%" }} placeholder="Describe your work, math and sources…" />
            <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center" }}>
              <button className="btn small lime" onClick={() => submit(lab.id)}>Submit for review</button>
              {msgs[lab.id] && <span className="micro">{msgs[lab.id]}</span>}
            </div>
          </div>
        ))}
        {capstone && <p className="micro" style={{ marginTop: 16 }}>Capstone: {capstone.title} — graded by a reviewer, then defended orally before Black Belt issuance.</p>}
        <Link className="textbutton" to="/academy">← Back to the academy</Link>
      </section>
    </>
  );
}

export function AcademyVerify() {
  return (
    <>
      <Seo title="Verify a certificate | Solar Black Belt Academy" />
      <section className="wrap" style={{ padding: "70px 20px", textAlign: "center" }} data-testid="academy-verify">
        <h1>Certificate verification</h1>
        <p className="lead">Verification links look like /academy/verify/&lt;code&gt;. Open the full link from a certificate to see its live status.</p>
      </section>
    </>
  );
}

export function AcademyCareers() {
  const [form, setForm] = useState({ roleInterest: "", cvUrl: "", portfolioUrl: "", qualifications: "", hiringConsent: false });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <>
      <Seo title="Hiring pipeline | Solar Black Belt Academy" description="Opt in to the Coastal Energy hiring pipeline after your academy training — installer, sales and support roles." />
      <section className="page-hero"><div className="wrap"><span className="eyebrow">CAREER PATHWAY</span><h1>Train, certify, get considered.</h1></div></section>
      <section className="wrap academy-section" style={{ maxWidth: 720 }}>
        <p className="lead">
          Finishing your belts lets you opt in to Coastal Energy's hiring pipeline for solar installer, sales and
          support roles. Completion enables consideration — it is never an automatic offer or contractor authorization.
        </p>
        <form onSubmit={async (e) => { e.preventDefault(); setError(""); try { await academyApi.careers(form); setMsg("Application saved to the hiring queue."); } catch (err) { setError(err.response?.data?.detail || "Failed."); } }} data-testid="academy-careers-form">
          <label className="field"><span>Role you're interested in</span>
            <input data-testid="academy-careers-role" value={form.roleInterest} onChange={set("roleInterest")} required minLength={2} maxLength={120} placeholder="e.g. Commercial solar installer" />
          </label>
          <label className="field"><span>CV link (optional)</span><input value={form.cvUrl} onChange={set("cvUrl")} maxLength={300} placeholder="https://…" /></label>
          <label className="field"><span>Portfolio link (optional)</span><input value={form.portfolioUrl} onChange={set("portfolioUrl")} maxLength={300} placeholder="https://…" /></label>
          <label className="field"><span>Verified qualifications (optional)</span><textarea rows={3} maxLength={1000} value={form.qualifications} onChange={set("qualifications")} placeholder="Licenses, certifications, experience…" /></label>
          <label className="academy-choice" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={form.hiringConsent} onChange={set("hiringConsent")} data-testid="academy-careers-consent" />
            I opt in to hiring consideration by Coastal Energy and understand this is not a job offer.
          </label>
          {error && <p className="micro" style={{ color: "#a33" }}>{error}</p>}
          {msg && <p className="micro" style={{ color: "var(--teal)" }} role="status">{msg}</p>}
          <button className="btn lime" data-testid="academy-careers-submit" disabled={!form.hiringConsent}>Join the hiring pipeline</button>
        </form>
      </section>
    </>
  );
}
